import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { rangeLabel } from "@/components/BookingPickers";

const HOURS = Array.from({ length: 12 }, (_, i) => 9 + i);
const today = () => new Date(Date.now() + 5.5 * 36e5).toISOString().slice(0, 10);

export default function SlotBlocker() {
  const [date, setDate] = useState(today());
  const [sel, setSel] = useState([]);
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadList = () => api.get("/admin/blocked-slots").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    api.get(`/slots/blocked?date=${date}`).then((r) => setSel(r.data.hours)).catch(() => setSel([]));
  }, [date]);

  const toggle = (h) => setSel((s) => (s.includes(h) ? s.filter((x) => x !== h) : [...s, h]));
  const save = async (hours = sel) => {
    setSaving(true);
    try {
      await api.post("/admin/blocked-slots", { date, hours });
      setSel(hours);
      toast.success(hours.length ? `Saved: ${hours.length} hour(s) closed on ${date}` : `All slots open on ${date}`);
      loadList();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="slot-blocker" className="space-y-6">
      <div className="chela-card rounded-3xl p-6 space-y-4" style={{ transform: "none" }}>
        <h2 className="font-display font-bold text-xl text-[#121316] flex items-center gap-2"><CalendarOff size={20} className="text-[#E05624]" /> Close slots</h2>
        <p className="text-sm text-[#5A5D65]">Pick a date, tap the hours you can't take (they turn red), then Save. Customers won't be able to book those hours.</p>
        <input data-testid="block-date" type="date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} className="h-12 px-4 rounded-xl border border-[#E5E0D8] bg-white font-bold" />
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {HOURS.map((h) => (
            <button key={h} type="button" data-testid={`block-hour-${h}`} onClick={() => toggle(h)}
              className={`h-12 rounded-xl border text-sm font-bold transition-colors ${sel.includes(h) ? "bg-[#C0392B] border-[#C0392B] text-white line-through" : "bg-white border-[#E5E0D8] text-[#121316]"}`}>
              {rangeLabel(h, 1)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="block-save" onClick={() => save()} disabled={saving} className="h-11 px-5 rounded-full btn-saffron font-bold inline-flex items-center gap-2 disabled:opacity-60"><Save size={16} /> Save</button>
          <button data-testid="block-whole-day" onClick={() => save(HOURS)} disabled={saving} className="h-11 px-5 rounded-full bg-[#121316] text-white font-bold disabled:opacity-60">Close whole day</button>
          <button data-testid="block-open-all" onClick={() => save([])} disabled={saving} className="h-11 px-5 rounded-full border border-[#E5E0D8] bg-white font-bold disabled:opacity-60">Open all</button>
        </div>
      </div>
      <div className="chela-card rounded-3xl p-6" style={{ transform: "none" }}>
        <h3 className="font-bold text-[#121316]">Upcoming closed slots</h3>
        {list.length === 0 && <p className="mt-2 text-sm text-[#5A5D65]">Nothing closed. All slots are open.</p>}
        <ul className="mt-3 space-y-2">
          {list.map((d) => (
            <li key={d.date} data-testid={`blocked-${d.date}`} className="flex flex-wrap items-center gap-2 text-sm">
              <button data-testid={`blocked-date-${d.date}`} onClick={() => setDate(d.date)} className="font-bold underline">{d.date}</button>
              <span className="text-[#5A5D65]">{d.hours.length === 12 ? "Whole day closed" : d.hours.map((h) => rangeLabel(h, 1)).join(", ")}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
